import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Network } from "lucide-react";
import { BodyFigure } from "./BodyFigure";
import { CONCERN_CHAINS, REGIONS } from "./chainData";
import { Reveal } from "@/components/site/Reveal";

// Per-concern "how the body connects" diagram with linked regions and reasons.
export function ConnectionDiagram({ slug, name }) {
  const chain = CONCERN_CHAINS[slug];
  const [focus, setFocus] = useState(null);
  if (!chain) return null;

  const linkIds = chain.links.map((l) => l.region);
  const pairs = (focus ? [focus] : linkIds).map((id) => [chain.primary, id]);
  const onSelect = (id) => { if (linkIds.includes(id)) setFocus(id); };

  return (
    <section className="bg-linen py-20 lg:py-28 px-6 md:px-10 lg:px-14" data-testid="condition-connection-diagram">
      <div className="max-w-[1500px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <Reveal className="lg:col-span-4">
          <div className="bg-ivory border border-espresso/10 rounded-sm p-8 flex justify-center lg:sticky lg:top-32">
            <div className="w-full max-w-[240px]">
              <BodyFigure lit={[chain.primary, ...linkIds]} active={focus || chain.primary} pairs={pairs} onSelect={onSelect} testid="condition-body-figure" />
              <div className="mt-4 text-center text-xs uppercase tracking-[0.18em] text-taupe">Where it shows up: <span className="text-espresso">{REGIONS[chain.primary].name}</span></div>
            </div>
          </div>
        </Reveal>
        <div className="lg:col-span-8">
          <Reveal>
            <div className="overline mb-5 flex items-center gap-2"><Network size={15} className="text-brass" /> Why it connects</div>
            <h2 className="font-serif font-light text-3xl lg:text-5xl text-espresso leading-tight mb-6">With {name.toLowerCase()}, where it shows up isn't always where it starts.</h2>
            <p className="text-lg font-light leading-relaxed text-espressosoft/85 max-w-2xl">{chain.intro}</p>
          </Reveal>
          <ul className="mt-10 divide-y divide-espresso/10 border-y border-espresso/10" onMouseLeave={() => setFocus(null)}>
            {chain.links.map((l, i) => (
              <Reveal as="li" key={l.region} delay={i * 70}>
                <button onMouseEnter={() => setFocus(l.region)} onFocus={() => setFocus(l.region)} onClick={() => setFocus(l.region)}
                  data-testid={`connection-link-${l.region}`}
                  className={`w-full text-left py-6 grid sm:grid-cols-12 gap-2 sm:gap-6 transition-colors duration-300 ${focus === l.region ? "text-espresso" : "text-espressosoft/80"}`}>
                  <span className="sm:col-span-4 flex items-center gap-3 font-serif text-2xl">
                    <span className={`h-2.5 w-2.5 rounded-full transition-colors duration-300 ${focus === l.region ? "bg-brass" : "bg-espresso/25"}`} />
                    {REGIONS[l.region].name}
                  </span>
                  <span className="sm:col-span-8 font-light leading-relaxed">{l.why}</span>
                </button>
              </Reveal>
            ))}
          </ul>
          <Link to="/approach" className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-espresso link-underline" data-testid="condition-explore-chain">
            Explore the full chain <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}
